import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, getToken, setToken } from "../lib/api";
import { PageShell } from "../components/PageShell";
import { AppText, Button, Card, Badge, Toggle } from "../components/ui";
import { Seo } from "../components/Seo";
import { CoachRules } from "../components/CoachRules";
import { CoachQueue } from "../components/CoachQueue";

const EMERGENT_AUTH = "https://auth.emergentagent.com/";

export default function Coach() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Retour d'auth Emergent: session_id dans le hash
    const hash = window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (m) {
      api.authSession(decodeURIComponent(m[1]))
        .then((res) => { setToken(res.session_token); window.location.hash = ""; setUser(res.user); setLoading(false); })
        .catch(() => { toast.error("Connexion coach échouée"); setLoading(false); });
      return;
    }
    if (getToken()) api.me().then(setUser).catch(() => setToken(null)).finally(() => setLoading(false));
    else setLoading(false);
  }, []);

  const login = () => {
    const redirect = `${window.location.origin}/coach`;
    window.location.href = `${EMERGENT_AUTH}?redirect=${encodeURIComponent(redirect)}`;
  };
  const logout = async () => { try { await api.logout(); } catch {} setToken(null); setUser(null); };

  if (loading) return <PageShell><div className="py-24 flex justify-center"><span className="h-8 w-8 animate-spin rounded-full border-4 border-km-coral border-t-transparent" /></div></PageShell>;

  if (!user || !user.is_admin) {
    return (
      <PageShell>
        <Seo title="Espace coach" description="Tableau de bord coach KM01." noindex />
        <Card className="mt-8 flex flex-col items-center text-center gap-4 py-10">
          <AppText variant="title">Espace coach</AppText>
          {user && !user.is_admin ? <AppText variant="body" className="text-km-error">Accès coach requis ({user.email} n'est pas autorisé).</AppText> : <AppText variant="body" className="text-km-ontertiary">Connecte-toi avec le compte Google autorisé.</AppText>}
          <Button title="Connexion Google" onClick={login} testID="coach-login" />
        </Card>
      </PageShell>
    );
  }

  return <Dashboard user={user} logout={logout} />;
}

function Dashboard({ user, logout }) {
  const [tab, setTab] = useState("queue");
  const [runnerToOpen, setRunnerToOpen] = useState(null);
  const openRunner = (id) => { setRunnerToOpen(id); setTab("runners"); };
  const TABS = [
    { k: "queue", label: "À regarder" },
    { k: "runners", label: "Coureurs" },
    { k: "sessions", label: "Séances" },
    { k: "stats", label: "Stats" },
    { k: "shoes", label: "Chaussures" },
    { k: "errors", label: "Erreurs" },
    { k: "testimonials", label: "Témoignages" },
    { k: "rules", label: "Règles" },
  ];
  return (
    <PageShell wide>
      <Seo title="Espace coach" description="Tableau de bord coach KM01." noindex />
      <div className="flex items-center justify-between mt-4">
        <AppText variant="display">Espace coach</AppText>
        <button onClick={logout} className="text-km-muted font-text font-bold" data-testid="coach-logout">Déconnexion</button>
      </div>
      <AppText variant="caption" className="text-km-muted mt-1">{user.email}</AppText>
      <div className="flex flex-wrap gap-2 mt-4">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`coach-tab-${t.k}`}
            className={`px-3 min-h-[40px] rounded-full font-text font-bold text-[14px] border-2 ${tab === t.k ? "border-km-coral bg-km-coralt text-km-oncoralt" : "border-km-border bg-white text-km-ink"}`}>{t.label}</button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "queue" && <CoachQueue onOpenRunner={openRunner} />}
        {tab === "runners" && <Runners initial={runnerToOpen} onConsumed={() => setRunnerToOpen(null)} />}
        {tab === "sessions" && <Sessions />}
        {tab === "stats" && <Stats />}
        {tab === "shoes" && <Shoes />}
        {tab === "errors" && <Errors />}
        {tab === "testimonials" && <Testimonials />}
        {tab === "rules" && <CoachRules />}
      </div>
    </PageShell>
  );
}

function useAsync(fn, deps = []) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const reload = () => fn().then(setData).catch((e) => setErr(e.message));
  useEffect(() => { reload(); }, deps); // eslint-disable-line
  return { data, err, reload };
}

function Runners({ initial = null, onConsumed }) {
  const { data, reload } = useAsync(api.coachRunners);
  const [selected, setSelected] = useState(initial);
  useEffect(() => { if (initial) { setSelected(initial); onConsumed && onConsumed(); } }, [initial]); // eslint-disable-line
  if (!data) return <Loader />;
  if (selected) return <RunnerDetail id={selected} onBack={() => { setSelected(null); reload(); }} />;
  if (data.length === 0) return <Empty text="Aucun coureur actif." />;
  return (
    <div className="flex flex-col gap-2" data-testid="coach-runners">
      {data.map((r) => {
        const p = r.program;
        return (
          <Card key={r.id} className="flex items-center justify-between p-4 cursor-pointer hover:border-km-coral" onClick={() => setSelected(r.id)} testID={`runner-${r.id}`}>
            <div><AppText variant="bodyStrong">{r.first_name || "—"} · {r.email}</AppText><AppText variant="caption" className="text-km-muted">{p ? `phase ${p.phase} · semaine ${p.week} · ${p.status}${p.inactive_days != null ? ` · ${p.inactive_days} j sans séance` : ""}` : "pas de programme"}{p?.access_until ? <span className={p.access_expired ? "text-km-error font-bold" : ""} data-testid={`access-${r.id}`}> · {p.access_expired ? "accès terminé le" : "accès jusqu'au"} {new Date(p.access_until).toLocaleDateString("fr-FR")}{p.access_expired ? " — plus d'emails" : ""}</span> : null}</AppText></div>
            {p?.coach_review ? <Badge text="À relire" tone="brand" /> : null}
          </Card>
        );
      })}
    </div>
  );
}

function DeleteRunnerButton({ id, email, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    const typed = window.prompt(`Suppression DÉFINITIVE de la fiche ${email} (programme, séances, messages, alertes, commandes).\nPour confirmer, tape : SUPPRIMER`);
    if (typed !== "SUPPRIMER") return;
    setBusy(true);
    try { const r = await api.coachDeleteRunner(id); toast.success(`Fiche ${r.email} supprimée`); onDeleted(); }
    catch (e) { toast.error(e.message || "Erreur"); setBusy(false); }
  };
  return <Button title="Supprimer la fiche" size="md" variant="secondary" onClick={del} loading={busy} className="!border-km-error !text-km-error" testID="runner-delete" />;
}

function RunnerDetail({ id, onBack }) {
  const { data, reload } = useAsync(() => api.coachRunner(id), [id]);
  if (!data) return <Loader />;
  const openQuestions = (data.alerts || []).filter((a) => a.type === "question_coureur" && !a.resolved);
  const chat = data.chat || [];
  return (
    <div className="flex flex-col gap-4" data-testid="runner-detail">
      <button onClick={onBack} className="self-start text-km-coral font-bold" data-testid="runner-back">← Retour</button>
      <Card>
        <AppText variant="subtitle">{data.first_name || "—"} · {data.email}</AppText>
        {data.program ? <AppText variant="caption" className="text-km-muted mt-1">phase {data.program.phase} · semaine {data.program.week} · {data.program.status} · {data.program.done_count} séances</AppText> : null}
        <div className="mt-3 flex flex-wrap items-center gap-2"><ExportRunnerButton id={id} /><DeleteRunnerButton id={id} email={data.email} onDeleted={onBack} /></div>
        {data.program?.coach_review ? <div className="mt-3"><ValidateProfile id={id} onDone={reload} /></div> : null}
      </Card>

      {data.level ? <LevelCard id={id} level={data.level} onDone={reload} /> : null}
      {data.level?.motor ? <MotorCard id={id} motor={data.level.motor} onDone={reload} /> : null}

      {openQuestions.map((a) => (
        <DraftReply key={a.id} runnerId={id} alert={a} onSent={reload} />
      ))}

      <Card>
        <AppText variant="bodyStrong" className="mb-2">Conversation</AppText>
        <div className="flex flex-col gap-2 max-h-[360px] overflow-y-auto">
          {chat.length === 0 ? <AppText variant="caption" className="text-km-muted">Aucun message.</AppText> : chat.map((m) => (
            <div key={m.id} className={`max-w-[85%] rounded-2xl px-3 py-2 ${m.role === "coach" ? "self-end bg-km-sage/15 border border-km-sage/40" : "self-start bg-km-coralt"}`}>
              <AppText variant="label" className={m.role === "coach" ? "text-km-sage mb-0.5" : "text-km-oncoralt mb-0.5"}>{m.role === "coach" ? "Éric" : "Coureur"}</AppText>
              <AppText variant="body" className="whitespace-pre-line text-[16px]">{m.text}</AppText>
            </div>
          ))}
        </div>
        <FreeMessage runnerId={id} onSent={reload} />
      </Card>
    </div>
  );
}

function LevelCard({ id, level, onDone }) {
  const [choice, setChoice] = useState(level.current || "");
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const currentLabel = (level.choices.find((c) => c.key === level.current) || {}).label || "non défini";
  const engine = level.engine || {};
  const engineKey = engine.montee ? (engine.montee[0] === 3 ? "montee_15_30" : "montee_plus") : engine.level != null ? String(engine.level) : null;
  const engineLabel = (level.choices.find((c) => c.key === engineKey) || {}).label || "—";
  const apply = async () => {
    if (!choice || choice === level.current) return;
    if (!window.confirm("Changer le niveau de départ ? La séance en attente sera régénérée à ce niveau.")) return;
    setBusy(true);
    try { const r = await api.coachSetLevel(id, choice, note, notify); toast.success(r.message ? (r.emailed ? "Niveau modifié — le coureur est prévenu (chat + email)" : "Niveau modifié — message déposé dans son chat") : "Niveau modifié"); onDone(); }
    catch (e) { toast.error(e.message || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <Card className="flex flex-col gap-3" testID="level-card">
      <div>
        <AppText variant="bodyStrong">Niveau de départ</AppText>
        <AppText variant="caption" className="text-km-muted">Actuel : <b>{currentLabel}</b>{level.set_by_coach ? ` · fixé par l'entraîneur le ${new Date(level.set_by_coach.at).toLocaleDateString("fr-FR")}` : ""}</AppText>
        <AppText variant="caption" className="text-km-muted">Calcul du moteur d'après le questionnaire : {engineLabel}</AppText>
      </div>
      {level.suggestion ? (
        <div className="rounded-xl bg-km-sunny/60 border border-km-borderstrong px-3 py-2 flex flex-wrap items-center gap-2" data-testid="level-suggestion">
          <AppText variant="caption" className="font-bold flex-1">Suggestion d'après le bilan : {level.suggestion.reason}</AppText>
          <button onClick={() => setChoice(level.suggestion.key)} className="text-km-coral font-bold text-[14px] min-h-[36px]" data-testid="level-suggestion-use">Reprendre</button>
        </div>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="level-inputs">
        {level.inputs.length === 0 ? <AppText variant="caption" className="text-km-muted">Aucune donnée de niveau dans le questionnaire.</AppText> : level.inputs.map((i) => (
          <div key={i.key} className="flex justify-between gap-3 border-b border-km-divider py-1"><AppText variant="caption" className="text-km-muted">{i.label}</AppText><AppText variant="caption" className="font-bold text-right">{i.value}</AppText></div>
        ))}
        {Object.entries(level.gait || {}).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 border-b border-km-divider py-1"><AppText variant="caption" className="text-km-sage">Bilan PDF · {{ longest_run_min: "plus longue course (min)", runs_per_week: "sorties / semaine", running_history: "expérience", gait_score: "score foulée", terrain: "profil", analysis_date: "date" }[k] || k}</AppText><AppText variant="caption" className="font-bold text-right">{String(v)}</AppText></div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <select value={choice} onChange={(e) => setChoice(e.target.value)} data-testid="level-select" className="min-h-[44px] flex-1 bg-white rounded-xl border-2 border-km-border px-3 font-text text-[15px] outline-none focus:border-km-coral">
          <option value="">— choisir un niveau —</option>
          {level.choices.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <Button title="Appliquer" size="md" onClick={apply} loading={busy} disabled={!choice || choice === level.current} testID="level-apply" />
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Une phrase pour le coureur (facultatif) : pourquoi ce niveau" data-testid="level-note" className="min-h-[44px] bg-white rounded-xl border-2 border-km-border px-3 font-text text-[15px] outline-none focus:border-km-coral" />
      <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="level-notify" className="w-5 h-5 accent-km-coral" /><AppText variant="caption">Prévenir le coureur (message dans son chat + email) avec ta phrase</AppText></label>
    </Card>
  );
}

function MotorCard({ id, motor, onDone }) {
  const [choice, setChoice] = useState(motor.current || "");
  const [busy, setBusy] = useState(false);
  const engine = motor.engine || {};
  const apply = async () => {
    if (!choice || choice === motor.current) return;
    setBusy(true);
    try { const r = await api.coachSetMotor(id, choice); toast.success(`Profil moteur : ${r.label} — la séance en attente est régénérée`); onDone(); }
    catch (e) { toast.error(e.message || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <Card className="flex flex-col gap-3" testID="motor-card">
      <div>
        <AppText variant="bodyStrong">Profil moteur</AppText>
        <AppText variant="caption" className="text-km-muted">Actuel : <b data-testid="motor-current">{motor.label || "—"}</b>{motor.score != null ? ` · moyenne ${String(motor.score).replace(".", ",")} / 10` : ""}{motor.set_by_coach ? ` · tranché par l'entraîneur le ${new Date(motor.set_by_coach.at).toLocaleDateString("fr-FR")}` : ""}</AppText>
        <AppText variant="caption" className="text-km-muted">Calcul d'après les notes du questionnaire : {engine.full_label || "—"}{engine.score != null ? ` (${String(engine.score).replace(".", ",")} / 10)` : ""} — 0-2,5 aérien de pieds · 2,5-5 aérien de cuisses · 5-7,5 terrien de pieds · + de 7,5 terrien de cuisses</AppText>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="motor-inputs">
        {motor.inputs.length === 0 ? <AppText variant="caption" className="text-km-muted">Pas de réponses au bloc D.</AppText> : motor.inputs.map((i) => (
          <div key={i.key} className="flex justify-between gap-3 border-b border-km-divider py-1"><AppText variant="caption" className="text-km-muted">{i.label}</AppText><AppText variant="caption" className="font-bold text-right">{i.value}{i.note != null ? ` · ${i.note}/10` : ""}</AppText></div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <select value={choice} onChange={(e) => setChoice(e.target.value)} data-testid="motor-select" className="min-h-[44px] flex-1 bg-white rounded-xl border-2 border-km-border px-3 font-text text-[15px] outline-none focus:border-km-coral">
          <option value="">— trancher le profil —</option>
          {motor.choices.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <Button title="Appliquer" size="md" onClick={apply} loading={busy} disabled={!choice || choice === motor.current} testID="motor-apply" />
      </div>
      <AppText variant="caption" className="text-km-muted">Les notes de chaque réponse se règlent dans l'onglet Règles → « 1bis · Profil moteur ».</AppText>
    </Card>
  );
}

function DraftReply({ runnerId, alert, onSent }) {
  const [text, setText] = useState(alert.suggested_reply || "");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (text.trim().length < 2) { toast.error("Message trop court"); return; }
    setSending(true);
    try { await api.coachMessage(runnerId, text.trim()); toast.success("Réponse envoyée au coureur (+ email)"); onSent(); }
    catch (e) { toast.error(e.message || "Erreur"); setSending(false); }
  };
  return (
    <Card className="border-2 border-km-coral" testID="draft-reply">
      <Badge text="Question en attente" tone="brand" />
      <AppText variant="body" className="mt-2">{alert.message}</AppText>
      <AppText variant="label" className="text-km-muted mt-3 mb-1">Réponse suggérée par l'IA — relis, corrige, puis envoie</AppText>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} data-testid="draft-text"
        className="w-full bg-white rounded-2xl border-2 border-km-border px-4 py-3 font-text text-[16px] outline-none focus:border-km-coral resize-y" />
      <div className="flex gap-2 mt-2">
        <Button title="Envoyer au coureur" onClick={send} loading={sending} size="md" testID="draft-send" />
        <Button title="Régénérer" variant="secondary" size="md" onClick={() => setText(alert.suggested_reply || "")} />
      </div>
    </Card>
  );
}

function FreeMessage({ runnerId, onSent }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (text.trim().length < 2) return;
    setSending(true);
    try { await api.coachMessage(runnerId, text.trim()); setText(""); toast.success("Message envoyé"); onSent(); }
    catch (e) { toast.error(e.message || "Erreur"); } finally { setSending(false); }
  };
  return (
    <div className="flex gap-2 items-end mt-3">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Écrire au coureur…" data-testid="coach-msg-input"
        className="flex-1 bg-white rounded-2xl border-2 border-km-border px-4 py-2 font-text text-[16px] outline-none focus:border-km-coral resize-none" />
      <Button title="Envoyer" size="md" onClick={send} loading={sending} testID="coach-msg-send" />
    </div>
  );
}

function ExportRunnerButton({ id }) {
  const [loading, setLoading] = useState(false);
  const run = async () => {
    setLoading(true);
    try {
      const { token } = await api.coachRunnerExportToken(id);
      const res = await fetch(api.coachRunnerExportUrl(id, token));
      if (!res.ok) throw new Error("Téléchargement impossible");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "km01_fiche_coureur.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      toast.success("Fiche PDF téléchargée");
    } catch (e) { toast.error(e.message || "Export impossible"); } finally { setLoading(false); }
  };
  return <Button title="Exporter la fiche (PDF)" size="md" variant="secondary" loading={loading} onClick={run} testID="export-runner" />;
}

function ValidateProfile({ id, onDone }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const validate = async () => {
    setSending(true);
    try { await api.coachValidateProfile(id, text.trim()); toast.success("Profil validé (+ email au coureur)"); onDone(); }
    catch (e) { toast.error(e.message || "Erreur"); setSending(false); }
  };
  return (
    <div className="flex flex-col gap-2">
      <AppText variant="label" className="text-km-muted">Ce profil sort du cadre — valide-le (mot optionnel)</AppText>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Un mot pour rassurer (facultatif)…"
        className="w-full bg-white rounded-2xl border-2 border-km-border px-4 py-2 font-text text-[16px] outline-none focus:border-km-coral resize-none" />
      <Button title="Valider le profil" size="md" onClick={validate} loading={sending} testID="validate-profile" />
    </div>
  );
}

function Sessions() {
  const mode = useAsync(api.coachMode);
  const pending = useAsync(api.coachPendingSessions);
  const codes = useAsync(api.coachTrialCodes);
  const [busy, setBusy] = useState("");
  if (!mode.data || !pending.data) return <Loader />;
  const runJob = async (key, fn, label) => {
    setBusy(key);
    try { const r = await fn(); toast.success(`${label} : ${r.sent} email(s) envoyé(s)`); }
    catch (e) { toast.error(e.message || "Erreur"); } finally { setBusy(""); }
  };
  const JOBS = [
    { key: "rem", label: "Rappels de la veille", fn: api.coachRunReminders },
    { key: "recap", label: "Récap du dimanche", fn: api.coachRunRecaps },
    { key: "exp", label: "Rappel fin d'accès (J-7)", fn: api.coachRunExpiry },
    { key: "nudge", label: "Relance inactifs (10 j)", fn: api.coachRunNudges },
    { key: "ana", label: "Contrôle des 3 mois", fn: api.coachRunAnalysis },
    { key: "bilan", label: "Relance bilan (3 semaines sans analyse)", fn: api.coachRunBilanNudges },
  ];
  return (
    <div className="flex flex-col gap-4" data-testid="coach-sessions">
      <Card className="flex items-center justify-between">
        <div><AppText variant="bodyStrong">Mode automatique</AppText><AppText variant="caption" className="text-km-muted">{mode.data.auto ? "Séances envoyées automatiquement, sans relecture" : `Relecture manuelle · ${mode.data.pending ?? pending.data.length} en attente`}</AppText></div>
        <button data-testid="mode-toggle" onClick={() => api.coachSetMode(!mode.data.auto).then(mode.reload).then(pending.reload)}><Toggle checked={mode.data.auto} /></button>
      </Card>

      <GuidanceCard />

      <StripeHealthCard />

      {mode.data.auto ? (
        <AppText variant="caption" className="text-km-muted">Mode auto activé : les séances partent directement aux coureurs. Désactive-le ci-dessus pour relire et modifier chaque séance avant envoi.</AppText>
      ) : pending.data.length === 0 ? (
        <Empty text="Aucune séance en attente de relecture." />
      ) : pending.data.map((s) => (
        <SessionReview key={s.session?.id} s={s} onSent={pending.reload} />
      ))}

      <Card>
        <AppText variant="bodyStrong">Emails automatiques</AppText>
        <AppText variant="caption" className="text-km-muted mt-1">Envoyés automatiquement chaque soir (18 h–22 h, heure de Paris) — récap le dimanche, fin d'accès à J-7. Tu peux aussi les déclencher maintenant :</AppText>
        <div className="flex flex-wrap gap-2 mt-3">
          {JOBS.map((j) => (
            <Button key={j.key} title={j.label} size="md" variant="secondary" loading={busy === j.key} testID={`email-${j.key}`}
              onClick={() => runJob(j.key, j.fn, j.label)} />
          ))}
        </div>
      </Card>

      {codes.data ? <TrialCodes /> : null}
    </div>
  );
}

function StripeHealthCard() {
  const { data } = useAsync(api.coachStripeHealth);
  if (!data) return <Loader />;
  const fmt = (d) => d ? new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
  const ok = data.webhook_ok;
  return (
    <Card className="flex flex-col gap-2" data-testid="stripe-health">
      <div className="flex items-center justify-between">
        <AppText variant="bodyStrong">Paiements Stripe</AppText>
        <span data-testid="stripe-status" className={`px-3 py-1 rounded-full text-[13px] font-text font-bold ${ok ? "bg-km-sage/30 text-km-ink" : "bg-km-coral text-white"}`}>
          {ok ? "Webhook OK" : "À vérifier"}
        </span>
      </div>
      <AppText variant="caption" className="text-km-muted">Mode : <b>{data.mode === "live" ? "réel (live)" : "test"}</b></AppText>
      {ok ? (
        <AppText variant="caption" className="text-km-ontertiary" data-testid="stripe-webhook-url">Stripe communique bien avec <b>{(data.expected_url || "").replace("https://", "").replace("/api/stripe/webhook", "")}</b>.</AppText>
      ) : (
        <AppText variant="caption" className="text-km-coral" data-testid="stripe-webhook-url">
          Le webhook pointe vers {data.webhook_url ? <b>{data.webhook_url.replace("https://", "").replace("/api/stripe/webhook", "")}</b> : "aucune adresse"} au lieu de <b>{(data.expected_url || "").replace("https://", "").replace("/api/stripe/webhook", "")}</b>. Il se recréera au prochain paiement, ou supprime l'ancien dans Stripe → Webhooks.
        </AppText>
      )}
      <div className="flex flex-col gap-1 mt-1 rounded-xl bg-km-tertiary p-3">
        <AppText variant="caption" className="text-km-muted">Dernier paiement reçu : <b className="text-km-ink">{data.last_payment ? `${fmt(data.last_payment.paid_at)} · ${data.last_payment.plan || ""}` : "aucun pour l'instant"}</b></AppText>
        <AppText variant="caption" className="text-km-muted">Dernier signal Stripe (webhook) : <b className="text-km-ink">{data.last_webhook ? `${fmt(data.last_webhook.received_at)}` : "aucun reçu"}</b></AppText>
      </div>
    </Card>
  );
}

function GuidanceCard() {
  const { data, reload } = useAsync(api.coachGuidance);
  const [text, setText] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data && !dirty) setText(data.text || ""); }, [data, dirty]);
  if (!data) return <Loader />;
  const save = async () => {
    setBusy(true);
    try { await api.coachSetGuidance(text); toast.success("Consignes enregistrées — appliquées aux prochaines séances"); setDirty(false); reload(); }
    catch (e) { toast.error(e.message || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <Card className="flex flex-col gap-2" data-testid="coach-guidance">
      <AppText variant="bodyStrong">Consignes à l'IA</AppText>
      <AppText variant="caption" className="text-km-muted">Tes consignes libres pour la rédaction des prochaines séances (ton, accents, points à insister). Elles ne changent jamais les durées ni les blocs décidés par la méthode.</AppText>
      <textarea value={text} onChange={(e) => { setText(e.target.value); setDirty(true); }} rows={4} data-testid="guidance-text"
        placeholder="Ex : insiste sur le plaisir et la régularité, varie les encouragements, rappelle de bien s'échauffer…"
        className="w-full bg-white rounded-xl border-2 border-km-border px-3 py-2 font-text text-[15px] text-km-ink outline-none focus:border-km-coral" />
      <Button title="Enregistrer les consignes" size="md" onClick={save} loading={busy} testID="guidance-save" className="self-start" />
    </Card>
  );
}

function SessionReview({ s, onSent }) {
  const ses = s.session || {};
  const t = ses.text || {};
  const body = (ses.session || {}).body || {};
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(t.title || "");
  const [intro, setIntro] = useState(t.intro || "");
  const [closing, setClosing] = useState(t.closing || "");
  const [busy, setBusy] = useState("");
  const field = "w-full bg-white rounded-xl border-2 border-km-border px-3 py-2 font-text text-[15px] text-km-ink outline-none focus:border-km-coral";
  const save = async () => {
    setBusy("save");
    try { await api.coachEditSession(s.program_id, ses.id, { title, intro, closing }); toast.success("Modifications enregistrées"); }
    catch (e) { toast.error(e.message || "Erreur"); } finally { setBusy(""); }
  };
  const send = async () => {
    setBusy("send");
    try { await api.coachSendSession(s.program_id, ses.id, { title, intro, closing }); toast.success("Séance envoyée au coureur"); onSent(); }
    catch (e) { toast.error(e.message || "Erreur"); setBusy(""); }
  };
  return (
    <Card className="flex flex-col gap-3 p-4" data-testid={`session-review-${ses.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <AppText variant="bodyStrong">{s.first_name || s.email || "Coureur"} · S{ses.week} · phase {ses.phase}</AppText>
          <AppText variant="caption" className="text-km-muted">{body.label || t.title || "Séance"}</AppText>
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid={`session-toggle-${ses.id}`} className="shrink-0 text-km-coral font-text font-bold min-h-[40px]">{open ? "Réduire" : "Voir / modifier"}</button>
      </div>
      {open ? (
        <>
          <div className="rounded-xl bg-km-tertiary p-3">
            <AppText variant="label" className="text-km-muted">Structure de la séance (décidée par la méthode — non modifiable)</AppText>
            <AppText variant="body" className="mt-1">{body.label || "—"}</AppText>
          </div>
          <div className="flex flex-col gap-1"><AppText variant="label" className="text-km-muted">Titre</AppText>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={field} data-testid={`edit-title-${ses.id}`} /></div>
          <div className="flex flex-col gap-1"><AppText variant="label" className="text-km-muted">Présentation (intro)</AppText>
            <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={4} className={field} data-testid={`edit-intro-${ses.id}`} /></div>
          <div className="flex flex-col gap-1"><AppText variant="label" className="text-km-muted">Encouragement (clôture)</AppText>
            <textarea value={closing} onChange={(e) => setClosing(e.target.value)} rows={2} className={field} data-testid={`edit-closing-${ses.id}`} /></div>
          <div className="flex gap-2">
            <Button title="Enregistrer" variant="secondary" size="md" onClick={save} loading={busy === "save"} testID={`session-save-${ses.id}`} className="flex-1" />
            <Button title="Envoyer au coureur" size="md" onClick={send} loading={busy === "send"} testID={`session-send-${ses.id}`} className="flex-1" />
          </div>
        </>
      ) : (
        <Button title="Envoyer au coureur" size="md" onClick={send} loading={busy === "send"} testID={`session-send-${ses.id}`} />
      )}
    </Card>
  );
}

function TrialCodes() {
  const { data, reload } = useAsync(api.coachTrialCodes);
  const [code, setCode] = useState("");
  const [days, setDays] = useState("15");
  const [maxUses, setMaxUses] = useState("");
  const [busy, setBusy] = useState(false);
  if (!data) return <Loader />;
  const add = async () => {
    if (code.trim().length < 3) { toast.error("Code trop court"); return; }
    setBusy(true);
    try {
      await api.coachAddTrialCode({ code: code.trim(), days: parseInt(days, 10) || 15, max_uses: maxUses.trim() ? parseInt(maxUses, 10) : null });
      toast.success("Code créé");
      setCode(""); setDays("15"); setMaxUses("");
      reload();
    } catch (e) { toast.error(e.message || "Erreur"); } finally { setBusy(false); }
  };
  const field = "min-h-[44px] bg-white rounded-xl border-2 border-km-border px-3 font-text font-semibold text-[15px] outline-none focus:border-km-coral";
  return (
    <Card data-testid="coach-trial-codes">
      <AppText variant="bodyStrong">Codes d'essai gratuit</AppText>
      <div className="flex flex-col gap-2 mt-3">
        {data.length === 0 ? <AppText variant="caption" className="text-km-muted">Aucun code pour l'instant.</AppText> : data.map((c) => (
          <div key={c.code} className="flex flex-col gap-1 rounded-xl bg-km-cream px-3 py-2" data-testid={`trial-${c.code}`}>
            <div className="flex items-center justify-between gap-2">
              <div>
                <AppText variant="bodyStrong">{c.code}</AppText>
                <AppText variant="caption" className="text-km-muted">{c.days} j · {c.uses}{c.max_uses ? `/${c.max_uses}` : ""} utilisation(s) · {c.active ? "actif" : "inactif"}</AppText>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => api.coachToggleTrialCode(c.code).then(reload)} data-testid={`trial-toggle-${c.code}`}><Toggle checked={c.active} /></button>
                <button onClick={() => api.coachDeleteTrialCode(c.code).then(reload)} className="text-km-error font-bold text-sm" data-testid={`trial-del-${c.code}`}>Suppr.</button>
              </div>
            </div>
            {(c.used_by || []).length ? (
              <div className="mt-1 flex flex-col gap-0.5 border-t border-km-divider pt-1" data-testid={`trial-usedby-${c.code}`}>
                {c.used_by.map((u, i) => (
                  <AppText key={i} variant="caption" className="text-km-muted">{u.first_name ? `${u.first_name} · ` : ""}{u.email}{u.at ? ` · ${String(u.at).slice(0, 10)}` : ""}</AppText>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-col gap-2 border-t border-km-divider pt-3">
        <AppText variant="label" className="text-km-muted">Créer un code</AppText>
        <div className="flex flex-wrap gap-2">
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Ex : KM01-PROMO" className={field + " flex-1 min-w-[140px]"} data-testid="new-code" />
          <input value={days} onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, ""))} placeholder="jours" className={field + " w-20"} data-testid="new-code-days" />
          <input value={maxUses} onChange={(e) => setMaxUses(e.target.value.replace(/[^0-9]/g, ""))} placeholder="usages (∞)" className={field + " w-24"} data-testid="new-code-max" />
          <Button title="Créer" size="md" onClick={add} loading={busy} testID="new-code-submit" />
        </div>
        <AppText variant="caption" className="text-km-muted">Durée en jours d'accès. Nombre d'usages vide = illimité. Chaque code reste utilisable une seule fois par profil.</AppText>
      </div>
    </Card>
  );
}


function Stats() {
  const { data } = useAsync(api.coachStats);
  if (!data) return <Loader />;
  return (
    <Card data-testid="coach-stats">
      <AppText variant="subtitle">Coureurs actifs par phase</AppText>
      <div className="flex flex-col gap-1 mt-2">
        {Object.entries(data.by_phase || data.phases || {}).map(([k, v]) => <AppText key={k} variant="body">Phase {k} : {v}</AppText>)}
      </div>
      {data.stripe_mode ? <AppText variant="caption" className="text-km-muted mt-3">Stripe : mode {data.stripe_mode}</AppText> : null}
    </Card>
  );
}

function Shoes() {
  const { data, reload } = useAsync(api.coachShoes);
  const onImport = async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const r = await api.coachImportShoes(f); toast.success(`${r.imported} chaussures importées`); reload(); } catch (err) { toast.error(err.message); } };
  if (!data) return <Loader />;
  const sorted = [...data].sort((a, b) => (a.status === "a_valider" ? 0 : 1) - (b.status === "a_valider" ? 0 : 1) || (a.label || "").localeCompare(b.label || ""));
  const toValidate = sorted.filter((s) => s.status === "a_valider").length;
  return (
    <div className="flex flex-col gap-3" data-testid="coach-shoes">
      <div className="flex items-center justify-between gap-2">
        <AppText variant="caption" className="text-km-muted">{data.length} modèle(s){toValidate ? ` · ${toValidate} à valider` : ""}</AppText>
        <label className="self-start"><span className="inline-flex px-4 py-2 rounded-full bg-km-coral text-white font-bold cursor-pointer text-[14px]">Importer un CSV</span><input type="file" accept=".csv" onChange={onImport} className="hidden" data-testid="shoes-import" /></label>
      </div>
      <div className="flex flex-col gap-2">{sorted.map((s) => <ShoeRow key={s.id} shoe={s} onSaved={reload} />)}</div>
    </div>
  );
}

function ShoeRow({ shoe, onSaved }) {
  const [f, setF] = useState({ ...shoe });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const num = (v) => { const n = parseFloat(String(v ?? "").replace(",", ".")); return isNaN(n) ? null : n; };
  const aValider = f.status === "a_valider";
  const inp = "w-full min-h-[42px] bg-white rounded-lg border-2 border-km-border px-2 font-text text-[15px] text-km-ink outline-none focus:border-km-coral";
  const save = async (validate) => {
    setBusy(true);
    try {
      await api.coachSaveShoe({ id: shoe.id, brand: f.brand, model: f.model, year: num(f.year) ? parseInt(num(f.year), 10) : null,
        stack_mm: num(f.stack_mm), drop_mm: num(f.drop_mm), softness: f.softness || "moyenne", rigidity: f.rigidity || "moyenne",
        status: validate ? "valide" : (f.status || "valide") });
      toast.success(validate ? "Chaussure validée" : "Enregistré");
      onSaved();
    } catch (e) { toast.error(e.message || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <Card className={`flex flex-col gap-2 p-3 ${aValider ? "border-2 border-km-coral" : ""}`} data-testid={`shoe-row-${shoe.id}`}>
      <div className="flex items-center justify-between gap-2">
        <AppText variant="bodyStrong" className="min-w-0 truncate">{shoe.label}{shoe.source === "runrepeat" ? " · runrepeat" : ""}</AppText>
        {aValider ? <span className="shrink-0 px-2 py-1 rounded-full bg-km-coral text-white text-[12px] font-bold">à valider</span>
          : <span className="shrink-0 px-2 py-1 rounded-full bg-km-sage/30 text-km-ink text-[12px] font-bold">validé</span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1"><AppText variant="caption" className="text-km-muted">Drop (mm)</AppText>
          <input value={f.drop_mm ?? ""} onChange={(e) => set("drop_mm", e.target.value)} inputMode="decimal" className={inp} data-testid={`shoe-drop-${shoe.id}`} /></label>
        <label className="flex flex-col gap-1"><AppText variant="caption" className="text-km-muted">Épaisseur talon (mm)</AppText>
          <input value={f.stack_mm ?? ""} onChange={(e) => set("stack_mm", e.target.value)} inputMode="decimal" className={inp} data-testid={`shoe-stack-${shoe.id}`} /></label>
        <label className="flex flex-col gap-1"><AppText variant="caption" className="text-km-muted">Dureté</AppText>
          <select value={f.softness || "moyenne"} onChange={(e) => set("softness", e.target.value)} className={inp} data-testid={`shoe-soft-${shoe.id}`}>
            <option value="souple">Souple</option><option value="moyenne">Moyenne</option><option value="ferme">Ferme</option></select></label>
        <label className="flex flex-col gap-1"><AppText variant="caption" className="text-km-muted">Flexibilité</AppText>
          <select value={f.rigidity || "moyenne"} onChange={(e) => set("rigidity", e.target.value)} className={inp} data-testid={`shoe-rigid-${shoe.id}`}>
            <option value="souple">Souple</option><option value="moyenne">Moyenne</option><option value="rigide">Rigide</option></select></label>
        <label className="flex flex-col gap-1"><AppText variant="caption" className="text-km-muted">Année</AppText>
          <input value={f.year ?? ""} onChange={(e) => set("year", e.target.value)} inputMode="numeric" className={inp} data-testid={`shoe-year-${shoe.id}`} /></label>
      </div>
      <div className="flex gap-2">
        <Button title="Enregistrer" variant="secondary" size="md" onClick={() => save(false)} loading={busy} testID={`shoe-save-${shoe.id}`} className="flex-1" />
        {aValider ? <Button title="Valider" size="md" onClick={() => save(true)} loading={busy} testID={`shoe-validate-${shoe.id}`} className="flex-1" /> : null}
      </div>
    </Card>
  );
}

function Errors() {
  const { data, reload } = useAsync(api.coachErrors);
  const [filter, setFilter] = useState("");
  const onImport = async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const r = await api.coachImportErrors(f); toast.success(`${r.imported} erreurs importées`); reload(); } catch (err) { toast.error(err.message); } };
  if (!data) return <Loader />;
  const rows = data.filter((x) => !filter || JSON.stringify(x).toLowerCase().includes(filter.toLowerCase()));
  return (
    <div className="flex flex-col gap-3" data-testid="coach-errors">
      <label className="self-start"><span className="inline-flex px-4 py-2 rounded-full bg-km-coral text-white font-bold cursor-pointer">Ré-importer le CSV (remplace la table)</span><input type="file" accept=".csv" onChange={onImport} className="hidden" data-testid="errors-import" /></label>
      <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer (numéro, thème, texte, condition…)" className="min-h-[48px] bg-white rounded-2xl border-2 border-km-border px-4 outline-none focus:border-km-coral" />
      <AppText variant="caption" className="text-km-muted">Table des {data.length} erreurs</AppText>
      <div className="flex flex-col gap-2">{rows.slice(0, 60).map((e) => (
        <Card key={e.number} className="p-3"><AppText variant="caption" className="text-km-muted">n° {e.number} · {e.theme} · prio {e.priority} · {e.context}</AppText><AppText variant="bodyStrong" className="line-through text-km-muted">« {e.phrase} »</AppText><AppText variant="bodyStrong">{e.answer}</AppText><AppText variant="caption" className="text-km-muted">{e.condition}</AppText></Card>
      ))}</div>
    </div>
  );
}

function Testimonials() {
  const { data, reload } = useAsync(api.coachTestimonials);
  if (!data) return <Loader />;
  if (data.length === 0) return <Empty text="Aucun témoignage pour l'instant." />;
  return (
    <div className="flex flex-col gap-2" data-testid="coach-testimonials">
      {data.map((t) => (
        <Card key={t.id} className="flex items-center justify-between gap-3 p-4">
          <div><Badge text={t.status} tone={t.status === "published" ? "sage" : "neutral"} /><AppText variant="body" className="mt-1">{t.first_name} · {t.text}</AppText></div>
          <div className="flex gap-2">
            {t.status !== "published" ? <Button title="Publier" size="md" onClick={() => api.coachModerateTestimonial(t.id, "approve").then(reload)} /> : <Button title="Retirer" size="md" variant="secondary" onClick={() => api.coachModerateTestimonial(t.id, "reject").then(reload)} />}
          </div>
        </Card>
      ))}
    </div>
  );
}

const Loader = () => <div className="py-10 flex justify-center"><span className="h-6 w-6 animate-spin rounded-full border-4 border-km-coral border-t-transparent" /></div>;
const Empty = ({ text }) => <Card><AppText variant="body" className="text-km-muted">{text}</AppText></Card>;
